import React, { useEffect, useState, useMemo } from "react";
import {
  FiCheckSquare,
  FiUpload,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiX,
} from "react-icons/fi";
import { getData, postData, putData, deleteData } from "../../../services/FetchNodeservices";
import Swal from "sweetalert2";

/* ================= DATE FORMAT ================= */
const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* ================= FILE TYPE ================= */
const getFileType = (file) => {
  if (!file) return null;
  const ext = file.split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png"].includes(ext)) return "image";
  if (["mp4", "mov", "avi"].includes(ext)) return "video";
  if (ext === "pdf") return "pdf";
  return null;
};

export default function TaskAssign() {
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const [previewFile, setPreviewFile] = useState(null);
  const [previewType, setPreviewType] = useState(null);

  const adminId = Number(localStorage.getItem("user_id"));

  const [form, setForm] = useState({
    task_name: "",
    task_description: "",
    assigned_to: "",
    deadline: "",
    file: null,
  });

  /* ================= FETCH ================= */
  useEffect(() => {
    fetchUsers();
    fetchTasks();
  }, []);

  const fetchUsers = async () => {
    const res = await getData("task/users");
    if (res?.status) setUsers(res.data || []);
  };

  const fetchTasks = async () => {
    setLoading(true);
    const res = await getData("task/all");
    if (res?.status) setTasks(res.data || []);
    setLoading(false);
  };

  /* ================= FORM ================= */
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setForm((prev) => ({ ...prev, [name]: files ? files[0] : value }));
  };

  const resetForm = () => {
    setForm({
      task_name: "",
      task_description: "",
      assigned_to: "",
      deadline: "",
      file: null,
    });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const fd = new FormData();
    fd.append("task_name", form.task_name);
    fd.append("task_description", form.task_description);
    fd.append("assigned_to", form.assigned_to);
    fd.append("assigned_by", adminId);
    fd.append("assigned_date", new Date().toISOString().split("T")[0]);
    fd.append("deadline", form.deadline);
    if (form.file) fd.append("attachment", form.file);

    try {
      const res = editingId
        ? await putData(`task/${editingId}`, fd, true)
        : await postData("task/assign", fd, true);

      if (res?.status) {
        Swal.fire({
          icon: "success",
          title: editingId ? "Task Updated" : "Task Assigned",
          timer: 1800,
          showConfirmButton: false,
        });
        resetForm();
        fetchTasks();
      }
    } catch {
      Swal.fire("Error", "Action failed", "error");
    }
  };

  /* ================= STATUS ================= */
  const updateStatus = async (id, status) => {
    await putData(`task/status/${id}`, { status });
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, status } : t))
    );
  };

  /* ================= DELETE ================= */
  const deleteTask = async (id) => {
    const ok = await Swal.fire({
      title: "Delete task?",
      text: "This cannot be undone",
      icon: "warning",
      showCancelButton: true,
    });

    if (!ok.isConfirmed) return;

    await deleteData(`task/${id}`);
    setTasks((prev) => prev.filter((t) => t.id !== id));
    Swal.fire("Deleted", "Task removed", "success");
  };

  /* ================= EDIT ================= */
  const editTask = (t) => {
    setEditingId(t.id);
    setForm({
      task_name: t.task_name,
      task_description: t.task_description,
      assigned_to: t.assigned_to,
      deadline: t.deadline || "",
      file: null,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ================= FILTER ================= */
  const filteredTasks = useMemo(() => {
    return tasks.filter(
      (t) =>
        t.task_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.assigned_to_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [tasks, searchTerm]);

  /* ================= PREVIEW ================= */
  const openPreview = (file) => {
    setPreviewFile(file);
    setPreviewType(getFileType(file));
  };

  /* ================= UI ================= */
  return (
    <div className="pt-6 pb-10 px-6 ml-0 md:ml-[220px] lg:ml-[260px] bg-[#f8fafc] min-h-screen">
      <div className="max-w-[1500px] mx-auto">

        {/* HEADER */}
        <div className="flex items-center gap-3 mb-8 mt-14">
          <div className="p-2 bg-indigo-600 rounded-xl text-white">
            <FiCheckSquare size={20} />
          </div>
          <h2 className="text-2xl font-black text-slate-800">
            {editingId ? "EDIT TASK" : "ASSIGN NEW TASK"}
          </h2>
        </div>

        {/* FORM */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow p-8 mb-10 grid grid-cols-1 md:grid-cols-4 gap-6"
        >
          <div className="md:col-span-2">
            <label className="label">Task Name</label>
            <input className="input" name="task_name" value={form.task_name} onChange={handleChange} required />
          </div>

          <div>
            <label className="label">Assign To</label>
            <select className="input" name="assigned_to" value={form.assigned_to} onChange={handleChange} required>
              <option value="">Select User</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Deadline</label>
            <input type="date" className="input" name="deadline" value={form.deadline} onChange={handleChange} />
          </div>

          <div className="md:col-span-4">
            <label className="label">Task Description</label>
            <textarea className="input h-28" name="task_description" value={form.task_description} onChange={handleChange} />
          </div>

          <div className="md:col-span-3">
            <label className="label">Attachment</label>
            <input type="file" name="file" onChange={handleChange} />
          </div>

          <div className="flex items-end justify-end">
            <button className="bg-indigo-600 text-white px-10 py-3 rounded-xl font-bold">
              {editingId ? "Update Task" : "Assign Task"}
            </button>
          </div>
        </form>

        {/* TABLE */}
        <div className="bg-white rounded-2xl shadow overflow-hidden">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="font-bold text-slate-700">Assigned Tasks</h3>
            <div className="relative">
              <FiSearch className="absolute left-3 top-2.5 text-slate-400" />
              <input className="pl-9 pr-3 py-2 border rounded-lg text-sm" placeholder="Search..." onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
          </div>

          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="p-4 text-left">Task</th>
                <th className="p-4 text-left">User</th>
                <th className="p-4 text-left">Assigned On</th>
                <th className="p-4 text-left">Deadline</th>
                <th className="p-4 text-left">Status</th>
                <th className="p-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="p-10 text-center">Loading...</td></tr>
              ) : filteredTasks.length === 0 ? (
                <tr><td colSpan="6" className="p-10 text-center">No tasks found</td></tr>
              ) : (
                filteredTasks.map((t) => (
                  <tr key={t.id} className="border-t">
                    <td className="p-4 font-medium">{t.task_name}</td>
                    <td className="p-4">{t.assigned_to_name}</td>
                    <td className="p-4">{formatDate(t.assigned_date)}</td>
                    <td className="p-4">{formatDate(t.deadline)}</td>
                    <td className="p-4">
                      <select value={t.status} onChange={(e) => updateStatus(t.id, e.target.value)} className="border rounded px-2 py-1">
                        <option>Pending</option>
                        <option>In Progress</option>
                        <option>Completed</option>
                      </select>
                    </td>
                    <td className="p-4 flex gap-3">
                      {t.attachment && (
                        <button onClick={() => openPreview(t.attachment)}><FiEye /></button>
                      )}
                      <button onClick={() => editTask(t)}><FiEdit2 /></button>
                      <button onClick={() => deleteTask(t.id)}><FiTrash2 /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PREVIEW MODAL */}
        {previewFile && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-4 max-w-3xl w-full relative">
              <button className="absolute top-3 right-3" onClick={() => setPreviewFile(null)}>
                <FiX size={18} />
              </button>

              {previewType === "image" && (
                <img src={`/uploads/tasks/${previewFile}`} alt="" className="max-h-[70vh] mx-auto" />
              )}

              {previewType === "video" && (
                <video controls className="max-h-[70vh] mx-auto">
                  <source src={`/uploads/tasks/${previewFile}`} />
                </video>
              )}

              {previewType === "pdf" && (
                <iframe
                  src={`/uploads/tasks/${previewFile}`}
                  className="w-full h-[70vh]"
                  title="PDF Preview"
                />
              )}
            </div>
          </div>
        )}

      </div>

      {/* COMMON INPUT STYLES */}
      <style>{`
        .input {
          width: 100%;
          padding: 0.75rem 1rem;
          border-radius: 0.75rem;
          border: 1px solid #e5e7eb;
          font-size: 0.875rem;
        }
        .label {
          font-size: 0.75rem;
          font-weight: 700;
          color: #64748b;
          margin-bottom: 0.25rem;
          display: block;
        }
      `}</style>
    </div>
  );
}
